import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import RealtimeMiniBoard from './RealtimeMiniBoard';
import RealtimeTickerDrawer, {
  RealtimeStockLatestResponse,
} from './RealtimeTickerDrawer';

export type RealtimeStockSummaryResponse = {
  totalCount: number;
  recentUpdatedCount: number;
  avgTradingValue: number | null;
  latestEventAt: string | null;
};

type SortByType =
  | 'last_event_at'
  | 'current_price'
  | 'cum_volume'
  | 'cum_trading_value'
  | 'ticker'
  | 'corp_name'
  | 'change_rate'
  | 'diff_from_prev';

type SortDirection = 'ASC' | 'DESC';

type PageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
};

type Props = {
  apiBaseUrl?: string;
  pollingMs?: number;
  compact?: boolean;
  maxRows?: number;
  defaultMarket?: 'all' | 'KOSPI' | 'KOSDAQ';
  showHeader?: boolean;
  showFilters?: boolean;
};

const neo = {
  border: 'rgba(120,140,180,.22)',
  border2: 'rgba(120,140,180,.14)',
  text: '#eaf2ff',
  muted: '#9fb4d8',
  soft: '#7f9cc3',
  pillBg: 'rgba(120,140,180,.10)',
  green: '#7DFFA7',
};

const initialPageResponse: PageResponse<RealtimeStockLatestResponse> = {
  content: [],
  totalElements: 0,
  totalPages: 0,
  size: 20,
  number: 0,
  first: true,
  last: true,
  empty: true,
};

function useIsMobile(breakpoint = 900) {
  const getIsMobile = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= breakpoint;
  }, [breakpoint]);

  const [isMobile, setIsMobile] = useState<boolean>(getIsMobile);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getIsMobile]);

  return isMobile;
}

function formatTradingValue(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const abs = Math.abs(value);
  if (abs >= 100000000) return `${(value / 100000000).toFixed(1)}억`;
  if (abs >= 10000) return `${(value / 10000).toFixed(0)}만`;
  return new Intl.NumberFormat('ko-KR').format(value);
}

function formatPercent(value?: number | null, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}%`;
}

function normalizePageResponse(
  raw: unknown,
  pageSize: number
): PageResponse<RealtimeStockLatestResponse> {
  if (Array.isArray(raw)) {
    return {
      content: raw as RealtimeStockLatestResponse[],
      totalElements: raw.length,
      totalPages: Math.max(Math.ceil(raw.length / pageSize), 1),
      size: pageSize,
      number: 0,
      first: true,
      last: raw.length <= pageSize,
      empty: raw.length === 0,
    };
  }

  const page = raw as Partial<PageResponse<RealtimeStockLatestResponse>>;

  return {
    content: Array.isArray(page.content) ? page.content : [],
    totalElements: typeof page.totalElements === 'number' ? page.totalElements : 0,
    totalPages: typeof page.totalPages === 'number' ? page.totalPages : 0,
    size: typeof page.size === 'number' ? page.size : pageSize,
    number: typeof page.number === 'number' ? page.number : 0,
    first: typeof page.first === 'boolean' ? page.first : true,
    last: typeof page.last === 'boolean' ? page.last : true,
    empty: typeof page.empty === 'boolean' ? page.empty : true,
  };
}

function getMaxLastEvent(items: RealtimeStockLatestResponse[]) {
  return (
    items
      .map((item) => item.lastEventAt)
      .filter((v): v is string => Boolean(v))
      .sort()
      .pop() ?? null
  );
}

function addNoCacheParam(searchParams: URLSearchParams) {
  searchParams.set('_ts', String(Date.now()));
}

function buildListUrl(params: {
  apiBaseUrl: string;
  size: number;
  keyword: string;
  market: string;
  sortBy: SortByType;
  sortDirection: SortDirection;
}) {
  const searchParams = new URLSearchParams();
  searchParams.set('page', '0');
  searchParams.set('size', String(params.size));
  searchParams.set('sortBy', params.sortBy);
  searchParams.set('sortDirection', params.sortDirection);

  if (params.keyword.trim()) searchParams.set('keyword', params.keyword.trim());
  if (params.market !== 'all') searchParams.set('market', params.market);

  addNoCacheParam(searchParams);

  return `${params.apiBaseUrl}?${searchParams.toString()}`;
}

function buildUpdatesUrl(params: {
  apiBaseUrl: string;
  since?: string | null;
  market: string;
  size: number;
}) {
  const searchParams = new URLSearchParams();
  searchParams.set('size', String(params.size));

  if (params.since) searchParams.set('since', params.since);
  if (params.market !== 'all') searchParams.set('market', params.market);

  addNoCacheParam(searchParams);

  return `${params.apiBaseUrl}/updates?${searchParams.toString()}`;
}

function buildSummaryUrl(params: { apiBaseUrl: string; market: string }) {
  const searchParams = new URLSearchParams();

  if (params.market !== 'all') searchParams.set('market', params.market);

  addNoCacheParam(searchParams);

  return `${params.apiBaseUrl}/summary?${searchParams.toString()}`;
}

const SummaryTile: React.FC<{
  title: string;
  value: string;
  description: string;
}> = ({ title, value, description }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: `1px solid ${neo.border}`,
      background: 'linear-gradient(180deg, rgba(8,24,47,0.94) 0%, rgba(7,18,35,0.94) 100%)',
    }}
  >
    <Typography sx={{ fontSize: 12, color: neo.soft, fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1.5 }}>
      <Typography sx={{ fontSize: 28, fontWeight: 900, color: '#f8fbff', lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#6f8cb2', textAlign: 'right', lineHeight: 1.45 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

export default function RealtimeMarketPanel({
  apiBaseUrl = '/api/realtime/stocks',
  pollingMs = 5000,
  compact = false,
  maxRows = 8,
  defaultMarket = 'all',
  showHeader = true,
  showFilters = true,
}: Props) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [market, setMarket] = useState<'all' | 'KOSPI' | 'KOSDAQ'>(defaultMarket);
  const [sortBy, setSortBy] = useState<SortByType>('last_event_at');
  const [sortDirection] = useState<SortDirection>('DESC');

  const [data, setData] = useState<PageResponse<RealtimeStockLatestResponse>>(initialPageResponse);
  const [summary, setSummary] = useState<RealtimeStockSummaryResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatedTickers, setUpdatedTickers] = useState<Record<string, number>>({});
  const [liveSeed, setLiveSeed] = useState(0);

  const [quickOpen, setQuickOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RealtimeStockLatestResponse | null>(null);

  const lastSinceRef = useRef<string | null>(null);
  const orderRef = useRef<string[]>([]);
  const pollingCountRef = useRef(0);
  const listAbortRef = useRef<AbortController | null>(null);

  const buildStockDetailUrl = useCallback((ticker: string) => {
    const todayStr = dayjs().format('YYYY-MM-DD');
    const fromStr = dayjs().subtract(60, 'day').format('YYYY-MM-DD');

    const params = new URLSearchParams();
    params.set('date', '');
    params.set('sector', '');
    params.set('theme', '');
    params.set('sort', 'volume');
    params.set('order', 'desc');
    params.set('limit', '100');
    params.set('view', 'net');
    params.set('q', ticker);
    params.set('from', fromStr);
    params.set('to', todayStr);
    params.set('expand', ticker);

    return `/stocks?${params.toString()}`;
  }, []);

  const handleTickerMore = useCallback(
    (ticker: string) => {
      navigate(buildStockDetailUrl(ticker));
    },
    [navigate, buildStockDetailUrl]
  );

  const fetchJson = useCallback(async <T,>(url: string, signal?: AbortSignal): Promise<T> => {
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal,
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json() as Promise<T>;
  }, []);

  const fetchList = useCallback(
    async (options?: { silent?: boolean; resetSince?: boolean }) => {
      if (options?.resetSince) {
        lastSinceRef.current = null;
      }

      listAbortRef.current?.abort();
      const controller = new AbortController();
      listAbortRef.current = controller;

      if (!options?.silent) {
        setLoading(true);
      }

      setError('');

      try {
        const url = buildListUrl({
          apiBaseUrl,
          size: maxRows,
          keyword,
          market,
          sortBy,
          sortDirection,
        });

        const raw = await fetchJson<unknown>(url, controller.signal);
        const normalized = normalizePageResponse(raw, maxRows);

        setData(normalized);
        orderRef.current = normalized.content.map((x) => x.ticker);

        const maxLastEvent = getMaxLastEvent(normalized.content);
        lastSinceRef.current = maxLastEvent;

        setSelectedItem((prev) => {
          if (!prev?.ticker) return prev;
          return normalized.content.find((x) => x.ticker === prev.ticker) || prev;
        });

        setLiveSeed((prev) => prev + 1);
      } catch (e: any) {
        if (e?.name === 'AbortError') return;

        if (!options?.silent) {
          setError('실시간 시장 패널 데이터를 불러오지 못했습니다.');
          setData(initialPageResponse);
        }
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [apiBaseUrl, keyword, market, sortBy, sortDirection, maxRows, fetchJson]
  );

  const fetchSummary = useCallback(async () => {
    try {
      const url = buildSummaryUrl({ apiBaseUrl, market });
      const result = await fetchJson<RealtimeStockSummaryResponse>(url);
      setSummary(result);
    } catch {
      setSummary(null);
    }
  }, [apiBaseUrl, market, fetchJson]);

  const fetchUpdates = useCallback(async () => {
    try {
      const url = buildUpdatesUrl({
        apiBaseUrl,
        since: lastSinceRef.current,
        market,
        size: Math.max(60, maxRows * 3),
      });

      const updates = await fetchJson<RealtimeStockLatestResponse[]>(url);
      if (!Array.isArray(updates) || !updates.length) return false;

      const now = Date.now();
      const tickers = updates.map((item) => item.ticker);

      setUpdatedTickers((prev) => {
        const next = { ...prev };
        tickers.forEach((tk) => {
          next[tk] = now;
        });
        return next;
      });

      setData((prev) => {
        const maxLastEvent = getMaxLastEvent(updates);
        if (maxLastEvent) lastSinceRef.current = maxLastEvent;

        const currentMap = new Map(prev.content.map((item) => [item.ticker, item]));
        const updateMap = new Map(updates.map((item) => [item.ticker, item]));

        const baseOrder = orderRef.current.length
          ? orderRef.current
          : prev.content.map((item) => item.ticker);

        const patchedVisible = baseOrder
          .map((ticker) => updateMap.get(ticker) || currentMap.get(ticker))
          .filter(Boolean) as RealtimeStockLatestResponse[];

        const existingSet = new Set(patchedVisible.map((item) => item.ticker));
        const appended = updates.filter((item) => !existingSet.has(item.ticker));

        const nextContent = [...patchedVisible, ...appended].slice(0, maxRows);

        orderRef.current = nextContent.map((x) => x.ticker);

        return {
          ...prev,
          content: nextContent,
          totalElements: Math.max(prev.totalElements, nextContent.length),
          totalPages: Math.max(prev.totalPages, 1),
          empty: nextContent.length === 0,
        };
      });

      setSelectedItem((prev) => {
        if (!prev) return prev;
        const next = updates.find((x) => x.ticker === prev.ticker);
        return next || prev;
      });

      setLiveSeed((prev) => prev + 1);
      return true;
    } catch {
      return false;
    }
  }, [apiBaseUrl, market, maxRows, fetchJson]);

  const refreshAll = useCallback(
    async (silent = false) => {
      await Promise.all([
        fetchList({ silent, resetSince: true }),
        fetchSummary(),
      ]);
    },
    [fetchList, fetchSummary]
  );

  useEffect(() => {
    refreshAll(false);

    return () => {
      listAbortRef.current?.abort();
    };
  }, [refreshAll]);

  useEffect(() => {
    const timer = window.setInterval(async () => {
      pollingCountRef.current += 1;

      const updated = await fetchUpdates();
      fetchSummary();

      // 업데이트 API가 since 기준으로 놓치는 경우 대비.
      // 3회 polling마다 전체 리스트를 silent 재조회해서 정렬/신규 종목/순위 변화를 반영.
      if (!updated || pollingCountRef.current % 3 === 0) {
        fetchList({ silent: true, resetSince: false });
      }
    }, pollingMs);

    return () => window.clearInterval(timer);
  }, [fetchUpdates, fetchSummary, fetchList, pollingMs]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();

      setUpdatedTickers((prev) => {
        const next: Record<string, number> = {};

        Object.entries(prev).forEach(([ticker, ts]) => {
          if (now - ts < 1800) next[ticker] = ts;
        });

        return next;
      });
    }, 800);

    return () => window.clearInterval(timer);
  }, []);

  const visibleItems = useMemo(() => data.content || [], [data.content]);

  const hottestItem = useMemo(() => {
    if (!visibleItems.length) return null;

    return [...visibleItems]
      .filter((item) => typeof item.changeRate === 'number')
      .sort((a, b) => (b.changeRate || 0) - (a.changeRate || 0))[0];
  }, [visibleItems]);

  const topTradingValueAvg = useMemo(() => {
    const values = visibleItems
      .map((item) => item.cumTradingValue || 0)
      .filter((value) => value > 0);

    if (!values.length) return null;

    return values.reduce((acc, cur) => acc + cur, 0) / values.length;
  }, [visibleItems]);

  const liveSelectedSnapshot = useMemo(() => {
    if (!selectedItem?.ticker) return null;
    return visibleItems.find((item) => item.ticker === selectedItem.ticker) || selectedItem;
  }, [visibleItems, selectedItem]);

  return (
    <Box
      sx={{
        borderRadius: 2.25,
        border: `1px solid ${neo.border}`,
        background: 'linear-gradient(180deg, rgba(8,24,47,0.95) 0%, rgba(7,18,35,0.95) 100%)',
        overflow: 'hidden',
      }}
    >
      {showHeader && (
        <Box
          sx={{
            px: 2,
            py: 1.6,
            borderBottom: `1px solid ${neo.border2}`,
            background: 'linear-gradient(180deg, rgba(9,31,61,0.9) 0%, rgba(8,24,47,0.65) 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 1,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: compact ? 18 : 22, fontWeight: 900, color: '#eef6ff' }}>
              장중 실시간 시장 패널
            </Typography>
            <Typography sx={{ mt: 0.6, fontSize: 12, color: neo.soft, fontWeight: 700 }}>
              실시간 관심 종목과 최근 갱신 흐름을 요약합니다.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refreshAll(false)}
            sx={{
              color: neo.text,
              borderColor: neo.border,
              borderRadius: 1.5,
              whiteSpace: 'nowrap',
              alignSelf: isMobile ? 'stretch' : 'flex-start',
            }}
          >
            새로고침
          </Button>
        </Box>
      )}

      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.2,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
            mb: 1.5,
          }}
        >
          <SummaryTile
            title="표시 종목 수"
            value={String(visibleItems.length)}
            description="현재 패널 기준"
          />
          <SummaryTile
            title="최근 3분 내 갱신"
            value={String(summary?.recentUpdatedCount ?? 0)}
            description="장중 최근 갱신 기준"
          />
          <SummaryTile
            title="평균 거래대금"
            value={topTradingValueAvg ? formatTradingValue(topTradingValueAvg) : '-'}
            description="표시 종목 평균"
          />
          <SummaryTile
            title="최고 변동률"
            value={hottestItem?.changeRate != null ? formatPercent(hottestItem.changeRate) : '-'}
            description={hottestItem ? `${hottestItem.corpName}` : '표시 종목 기준'}
          />
        </Box>

        {showFilters && (
          <Box
            sx={{
              display: 'grid',
              gap: 1.2,
              gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr 1fr auto',
              mb: 1.5,
            }}
          >
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setKeyword(searchInput.trim());
                }
              }}
              placeholder="종목명 또는 티커 검색"
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: neo.text,
                  borderRadius: 1.5,
                  background: 'rgba(8, 19, 36, 0.95)',
                  '& fieldset': { borderColor: neo.border },
                },
              }}
            />

            <TextField
              select
              size="small"
              label="시장"
              value={market}
              onChange={(e) => setMarket(e.target.value as 'all' | 'KOSPI' | 'KOSDAQ')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: neo.text,
                  borderRadius: 1.5,
                  background: 'rgba(8, 19, 36, 0.95)',
                  '& fieldset': { borderColor: neo.border },
                },
                '& .MuiInputLabel-root': { color: neo.soft },
              }}
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="KOSPI">KOSPI</MenuItem>
              <MenuItem value="KOSDAQ">KOSDAQ</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="정렬"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortByType)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: neo.text,
                  borderRadius: 1.5,
                  background: 'rgba(8, 19, 36, 0.95)',
                  '& fieldset': { borderColor: neo.border },
                },
                '& .MuiInputLabel-root': { color: neo.soft },
              }}
            >
              <MenuItem value="last_event_at">최근 갱신순</MenuItem>
              <MenuItem value="cum_trading_value">거래대금순</MenuItem>
              <MenuItem value="cum_volume">거래량순</MenuItem>
              <MenuItem value="change_rate">등락률순</MenuItem>
            </TextField>

            <Button
              variant="contained"
              onClick={() => setKeyword(searchInput.trim())}
              sx={{
                borderRadius: 1.5,
                fontWeight: 900,
                background:
                  'linear-gradient(180deg, rgba(17, 58, 112, 0.95) 0%, rgba(10, 38, 78, 0.95) 100%)',
              }}
            >
              적용
            </Button>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap">
          <Chip label="장중 요약" size="small" sx={{ color: neo.green, fontWeight: 900 }} />
          <Chip label={`실시간 ${summary?.recentUpdatedCount ?? 0}건`} size="small" sx={{ color: neo.text }} />
          <Chip label="클릭 시 빠른 요약" size="small" sx={{ color: neo.text }} />
        </Stack>

        {loading ? (
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${neo.border}`,
              background:
                'linear-gradient(180deg, rgba(8,24,47,0.94) 0%, rgba(7,18,35,0.94) 100%)',
              p: 2,
              color: '#c9defb',
              fontSize: 14,
            }}
          >
            실시간 시장 패널 데이터를 불러오는 중입니다.
          </Box>
        ) : error ? (
          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(251,113,133,0.22)',
              background:
                'linear-gradient(180deg, rgba(54, 16, 28, 0.95) 0%, rgba(42, 11, 22, 0.95) 100%)',
              p: 2,
              color: '#ffc2cf',
              fontSize: 14,
            }}
          >
            {error}
          </Box>
        ) : (
          <RealtimeMiniBoard
            items={visibleItems}
            updatedTickers={updatedTickers}
            isMobile={isMobile}
            onOpenQuick={(item) => {
              setSelectedItem(item);
              setQuickOpen(true);
            }}
          />
        )}
      </Box>

      <RealtimeTickerDrawer
        open={quickOpen}
        item={selectedItem}
        apiBaseUrl={apiBaseUrl}
        pollingMs={4000}
        liveSeed={liveSeed}
        liveSelectedSnapshot={liveSelectedSnapshot}
        onClose={() => setQuickOpen(false)}
        onOpenTickerDetail={handleTickerMore}
      />
    </Box>
  );
}